import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { addYears, format, subDays } from 'date-fns';
import { listUserClub } from '../../../lib/api/club';
import { list as listContacts } from '../../../lib/api/contact';
import { listPlayers as listMembershipPlayers } from '../../../lib/api/contract';
import { listContractDurations, listContractTerminations } from '../../../lib/api/contract';
import { currentSeason } from '../../../lib/api/season';
import { useAppContext } from 'providers/AppProvider';
import { toast } from 'react-toastify';
import {
  createInitialContract,
  formatDateTime,
  getClubGuide,
  getClubLabel,
  getClubValue,
  getContactLabel,
  getContactValue,
  getDurationLabel,
  getDurationValue
} from './formUtils';

export function ContractForm({
  initialContract,
  mode = 'add',
  onSubmit,
  onPrint,
  submitLabel = 'Sacuvaj'
}) {
  const { userApp } = useAppContext();
  const [clubs, setClubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [durations, setDurations] = useState([]);
  const [terminations, setTerminations] = useState([]);
  const [seasonStart, setSeasonStart] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [contract, setContract] = useState(createInitialContract(initialContract));
  const [errors, setErrors] = useState({
    club_entities_id: false,
    players_entities_id: false,
    authorized_contact_id: false,
    contract_durations_id: false,
    valid_to: false,
    club_scan_document_path: false,
    signed_scan_document_path: false
  });

  const isReadOnly = mode === 'view';
  const isAddMode = mode === 'add';
  const isClubUser = userApp?.role_type_id === 'clubs';
  const getDocumentUrl = path => {
    if (!path || typeof path !== 'string') {
      return null;
    }

    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    return `${process.env.REACT_APP_BASE_API_URL}/uploads/${path}`;
  };
  const clubDocumentUrl = getDocumentUrl(initialContract?.club_scan_document_path);
  const federationDocumentUrl = getDocumentUrl(initialContract?.signed_scan_document_path);

  useEffect(() => {
    setContract(createInitialContract(initialContract));
  }, [initialContract]);

  useEffect(() => {
    (async () => {
      const [clubRows, durationRows, terminationRows, seasonRows] = await Promise.all([
        listUserClub(),
        listContractDurations(),
        listContractTerminations(),
        currentSeason()
      ]);

      setClubs(clubRows || []);
      setDurations(durationRows || []);
      setTerminations(terminationRows || []);

      const [seasonRow] = seasonRows || [];
      setSeasonStart(seasonRow?.season_start || null);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!initialContract?.players_entities_id) {
        setSelectedPlayer(null);
        return;
      }

      const [player] = await listMembershipPlayers({
        entities_id: initialContract.players_entities_id
      });

      if (!player) {
        setSelectedPlayer(null);
        return;
      }

      setSelectedPlayer({
        value: player.entities_id,
        label: `${player.name}${player.date_of_birth ? ` (${formatDateTime(player.date_of_birth)})` : ''}`
      });
    })();
  }, [initialContract]);

  const selectedClub = useMemo(
    () =>
      clubs.find(club => String(getClubValue(club)) === String(contract.club_entities_id)),
    [clubs, contract.club_entities_id]
  );

  useEffect(() => {
    (async () => {
      if (!selectedClub) {
        setContacts([]);
        return;
      }

      const clubGuide = getClubGuide(selectedClub);
      if (!clubGuide) {
        setContacts([]);
        return;
      }

      const contactRows = await listContacts(clubGuide);

      setContacts(contactRows || []);
    })();
  }, [selectedClub]);

  const handelInput = e => {
    const { name, value, files, type } = e.target;

    if (type === 'file') {
      const file = files?.[0] || null;

      setContract(prev => ({
        ...prev,
        [name]: file
      }));

      setErrors(prev => ({
        ...prev,
        [name]: Boolean(file instanceof File && !file.name.toLowerCase().endsWith('.pdf'))
      }));
      return;
    }

    if (name === 'club_entities_id') {
      setSelectedPlayer(null);
      setContract(prev => ({
        ...prev,
        club_entities_id: value,
        players_entities_id: '',
        authorized_contact_id: ''
      }));
      return;
    }

    if (name === 'contract_durations_id') {
      const selectedDuration = durations.find(
        duration => String(getDurationValue(duration)) === String(value)
      );
      const durationYears = Number(selectedDuration?.duration_years);
      let validTo = contract.valid_to;

      if (seasonStart && Number.isFinite(durationYears)) {
        const calculatedDate = subDays(addYears(new Date(seasonStart), durationYears), 1);
        validTo = format(calculatedDate, 'yyyy-MM-dd');
      }

      setContract(prev => ({
        ...prev,
        contract_durations_id: value,
        valid_to: validTo
      }));
      return;
    }

    setContract(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const loadPlayerOptions = (inputValue, callback) => {
    if (inputValue.length < 3 || !contract.club_entities_id) {
      return callback([]);
    }

    listMembershipPlayers({
      name: inputValue,
      club_entities_id: contract.club_entities_id
    })
      .then(response => {
        const options = response.map(player => ({
          value: player.entities_id,
          label: `${player.name} (${formatDateTime(player.date_of_birth)})`
        }));
        callback(options);
      })
      .catch(error => {
        console.error(error);
        callback([]);
      });
  };

  const validateForm = event => {
    const nextErrors = {
      club_entities_id: !contract.club_entities_id,
      players_entities_id: !contract.players_entities_id,
      authorized_contact_id: !contract.authorized_contact_id,
      contract_durations_id: !contract.contract_durations_id,
      valid_to: !contract.valid_to,
      club_scan_document_path: Boolean(
        contract.club_scan_document_path instanceof File &&
          !contract.club_scan_document_path.name?.toLowerCase().endsWith('.pdf')
      ),
      signed_scan_document_path: Boolean(
        contract.signed_scan_document_path instanceof File &&
          !contract.signed_scan_document_path.name?.toLowerCase().endsWith('.pdf')
      )
    };

    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      event.stopPropagation();
      return false;
    }

    return true;
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!validateForm(event)) {
      return;
    }

    await onSubmit(contract);
  };

  const handlePrint = async event => {
    event.preventDefault();

    if (!validateForm(event)) {
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Preglednik je blokirao prozor za stampu.');
      return;
    }

    printWindow.document.write(
      '<html><body style="font-family:sans-serif;padding:24px">Priprema dokumenta za stampu...</body></html>'
    );

    try {
      setIsPrinting(true);
      await onPrint(contract, printWindow);
    } catch (error) {
      printWindow.close();
      toast.error(error.body?.message || error.message || 'Stampanje ugovora nije uspjelo.');
    } finally {
      setIsPrinting(false);
    }
  };

  const renderDocumentInfo = (label, url) => (
    <Form.Group as={Col} md="12">
      <Form.Label>{label}</Form.Label>
      <div className="small" style={{ minHeight: '20px' }}>
        {url ? (
          <a href={url} target="_blank" rel="noreferrer">
            Trenutni dokument
          </a>
        ) : (
          <span className="text-muted">Nema dokumenta</span>
        )}
      </div>
    </Form.Group>
  );

  return (
    <Card>
      <Card.Body className="bg-body-tertiary">
        <Form method="POST" encType="multipart/form-data" noValidate onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="name">
              <Form.Label>Naziv</Form.Label>
              <Form.Control type="text" value="Ugovor" disabled />
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="date_created">
              <Form.Label>Datum</Form.Label>
              <Form.Control
                type="text"
                value={format(new Date(contract.date_created || new Date()), 'dd.MM.yyyy')}
                disabled
              />
            </Form.Group>
          </Row>

          <div className="border-dashed border-bottom my-3" />

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="club_entities_id">
              <Form.Label>Klub</Form.Label>
              <Form.Select
                name="club_entities_id"
                value={contract.club_entities_id}
                onChange={handelInput}
                isInvalid={errors.club_entities_id}
                disabled={isReadOnly}
              >
                <option value="">Izaberi</option>
                {clubs.map(club => (
                  <option key={String(getClubValue(club))} value={getClubValue(club)}>
                    {getClubLabel(club)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">Molimo izaberite klub.</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="players_entities_id">
              <Form.Label>Igrac</Form.Label>
              <AsyncSelect
                name="players_entities_id"
                cacheOptions
                defaultOptions={false}
                loadOptions={loadPlayerOptions}
                placeholder="Pretrazi..."
                classNamePrefix="react-select"
                onChange={selected => {
                  setSelectedPlayer(selected || null);
                  setContract(prev => ({
                    ...prev,
                    players_entities_id: selected ? selected.value : ''
                  }));
                }}
                value={selectedPlayer}
                isDisabled={!contract.club_entities_id || isReadOnly}
                isClearable
              />
              {errors.players_entities_id && (
                <div className="invalid-feedback d-block">Molimo izaberite igraca.</div>
              )}
            </Form.Group>
          </Row>

          <div className="border-dashed border-bottom my-3" />

          <Row className="mb-3">
            <Form.Group as={Col} md="12" controlId="authorized_contact_id">
              <Form.Label>Ovlasceno lice</Form.Label>
              <Form.Select
                name="authorized_contact_id"
                value={contract.authorized_contact_id}
                onChange={handelInput}
                isInvalid={errors.authorized_contact_id}
                disabled={!contract.club_entities_id || isReadOnly}
              >
                <option value="">Izaberi</option>
                {contacts.map(contact => (
                  <option key={String(getContactValue(contact))} value={getContactValue(contact)}>
                    {getContactLabel(contact)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Molimo izaberite ovlasceno lice.
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="contract_durations_id">
              <Form.Label>Trajanje ugovora</Form.Label>
              <Form.Select
                name="contract_durations_id"
                value={contract.contract_durations_id}
                onChange={handelInput}
                isInvalid={errors.contract_durations_id}
                disabled={isReadOnly}
              >
                <option value="">Izaberi</option>
                {durations.map(duration => (
                  <option
                    key={String(getDurationValue(duration))}
                    value={getDurationValue(duration)}
                  >
                    {getDurationLabel(duration)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Molimo izaberite trajanje ugovora.
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="6" controlId="valid_to">
              <Form.Label>Vazi do</Form.Label>
              <Form.Control
                type="date"
                name="valid_to"
                value={contract.valid_to}
                onChange={handelInput}
                isInvalid={errors.valid_to}
                disabled
              />
              <Form.Control.Feedback type="invalid">Molimo unesite datum vazenja.</Form.Control.Feedback>
            </Form.Group>
          </Row>

          {mode === 'edit' && onPrint && (
            <Row className="mb-3">
              <Col>
                <Button
                  type="button"
                  variant="falcon-primary"
                  onClick={handlePrint}
                  disabled={isPrinting}
                >
                  {isPrinting ? 'Priprema stampe...' : 'Stampaj ugovor za potpis'}
                </Button>
              </Col>
            </Row>
          )}

          {!isAddMode &&
            (isReadOnly ? (
              <Row className="mb-3">
                {federationDocumentUrl
                  ? renderDocumentInfo(
                      'Ugovor potpisan od strane saveza (PDF)',
                      federationDocumentUrl
                    )
                  : clubDocumentUrl
                    ? renderDocumentInfo(
                        'Ugovor potpisan od strane kluba (PDF)',
                        clubDocumentUrl
                      )
                    : renderDocumentInfo('Ugovor potpisan od strane kluba (PDF)', null)}
              </Row>
            ) : (
              <Row className="mb-3">
                <Form.Group
                  as={Col}
                  md={isClubUser ? '12' : '6'}
                  controlId="club_scan_document_path"
                >
                  <Form.Label>Ugovor potpisan od strane kluba (PDF)</Form.Label>
                  <div className="mb-2 small" style={{ minHeight: '20px' }}>
                    {clubDocumentUrl ? (
                      <a href={clubDocumentUrl} target="_blank" rel="noreferrer">
                        Trenutni dokument
                      </a>
                    ) : (
                      <span className="text-muted">Nema dokumenta</span>
                    )}
                  </div>
                  <Form.Control
                    type="file"
                    name="club_scan_document_path"
                    accept=".pdf,application/pdf"
                    onChange={handelInput}
                    isInvalid={errors.club_scan_document_path}
                  />
                  <Form.Control.Feedback type="invalid">
                    Dozvoljen je samo PDF fajl.
                  </Form.Control.Feedback>
                </Form.Group>
                {!isClubUser && (
                  <Form.Group as={Col} md="6" controlId="signed_scan_document_path">
                    <Form.Label>Ugovor potpisan od strane saveza (PDF)</Form.Label>
                    <div className="mb-2 small" style={{ minHeight: '20px' }}>
                      {federationDocumentUrl ? (
                        <a href={federationDocumentUrl} target="_blank" rel="noreferrer">
                          Trenutni dokument
                        </a>
                      ) : (
                        <span className="text-muted">Nema dokumenta</span>
                      )}
                    </div>
                    <Form.Control
                      type="file"
                      name="signed_scan_document_path"
                      accept=".pdf,application/pdf"
                      onChange={handelInput}
                      isInvalid={errors.signed_scan_document_path}
                    />
                    <Form.Control.Feedback type="invalid">
                      Dozvoljen je samo PDF fajl.
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              </Row>
            ))}

          {isReadOnly && contract.contract_terminations_id && (
            <>
              <div className="border-dashed border-bottom my-3" />
              <Row className="mb-3">
                <Form.Group as={Col} md="6" controlId="contract_terminations_id_view">
                  <Form.Label>Razlog raskida</Form.Label>
                  <Form.Select value={contract.contract_terminations_id || ''} disabled>
                    <option value="">Izaberi</option>
                    {terminations.map(termination => (
                      <option
                        key={String(termination.contract_terminations_id)}
                        value={termination.contract_terminations_id}
                      >
                        {termination.name || termination.contract_termination_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group as={Col} md="6" controlId="terminated_at_view">
                  <Form.Label>Datum raskida</Form.Label>
                  <Form.Control type="date" value={contract.terminated_at || ''} disabled />
                </Form.Group>
              </Row>
            </>
          )}

          {!isReadOnly && (
            <>
              <div className="border-dashed border-bottom my-3" />
              <Button type="submit">{submitLabel}</Button>
            </>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
}
